// NOTE: NSAI-5820 — turns a condense result into the one sentence a client
// shows for it. Shared so web and mobile answer the same button with the same
// words, and so a new status cannot be silently ignored by one platform: the
// switch below is exhaustive over the enum, which makes adding a status a
// compile error here rather than a blank toast in front of a user.

import type {
  IContextCondenseResult,
  IContextCondenseView,
  TContextCondenseMessageKey,
  TContextCondenseTone,
} from "../types";
import { EContextCondenseStatus } from "../types";

interface ILiftContextCondenseResultProps {
  result: IContextCondenseResult;
}

const MESSAGE_KEY_BY_STATUS: Record<
  EContextCondenseStatus,
  TContextCondenseMessageKey
> = {
  [EContextCondenseStatus.CONDENSED]: "contextPressure.condensed",
  [EContextCondenseStatus.NOTHING_TO_CONDENSE]:
    "contextPressure.condenseNothing",
  [EContextCondenseStatus.UNAVAILABLE]: "contextPressure.condenseUnavailable",
  [EContextCondenseStatus.IN_PROGRESS]: "contextPressure.condenseInProgress",
  [EContextCondenseStatus.FAILED]: "contextPressure.condenseFailed",
};

const TONE_BY_STATUS: Record<EContextCondenseStatus, TContextCondenseTone> = {
  [EContextCondenseStatus.CONDENSED]: "success",
  [EContextCondenseStatus.NOTHING_TO_CONDENSE]: "notice",
  [EContextCondenseStatus.UNAVAILABLE]: "notice",
  [EContextCondenseStatus.IN_PROGRESS]: "notice",
  [EContextCondenseStatus.FAILED]: "failure",
};

export const liftContextCondenseResult = ({
  result,
}: ILiftContextCondenseResultProps): IContextCondenseView => {
  const isSuccess = result.status === EContextCondenseStatus.CONDENSED;

  return {
    // NOTE: Both fallbacks cover a status this client does not know — an older
    // build against a newer server. TypeScript considers them unreachable; the
    // wire does not. Failing pessimistically keeps a user from being told room
    // was freed when nothing known happened, and `t()` on an undefined key
    // would render raw key text.
    tone: TONE_BY_STATUS[result.status] ?? "failure",
    messageKey:
      MESSAGE_KEY_BY_STATUS[result.status] ?? "contextPressure.condenseFailed",
    // NOTE: Only a real condense reports a count. Guards against a server that
    // sends a stale number alongside a non-success status.
    condensedMessageCount: isSuccess
      ? Math.max(0, result.condensedMessageCount)
      : 0,
  };
};
